import { useEffect, useState, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { useTelegram } from './hooks/useTelegram'
import { api, RoundData, Winner } from './lib/api'
import { Header } from './components/Header'
import { RoundInfo } from './components/RoundInfo'
import { BuyTickets } from './components/BuyTickets'
import { YourTickets } from './components/YourTickets'
import { Winners } from './components/Winners'
import { ClaimWizard, type ClaimApi } from './components/ClaimWizard'
import { InviteFriends } from './components/InviteFriends'

// The two claim paths differ only in their endpoints.
const prizeClaim: ClaimApi = {
  validate: (a) => api.validatePayout(a),
  initiate: (a) => api.initiatePayout(a),
  confirm: (id) => api.confirmPayout(id),
  cancel: (id) => api.cancelPayout(id),
  active: () => api.getActiveSession(),
}
const rewardClaim: ClaimApi = {
  validate: (a) => api.validateReferrerPayout(a),
  initiate: (a) => api.initiateReferrerPayout(a),
  confirm: (id) => api.confirmReferrerPayout(id),
  // One cancel endpoint for both, as the old referrer flow used.
  cancel: (id) => api.cancelPayout(id),
  active: () => api.getActiveSession(),
}

export default function App() {
  const { user, isReady, isTelegram, expand } = useTelegram()
  const { t } = useTranslation()

  const [round, setRound] = useState<RoundData | null>(null)
  const [winners, setWinners] = useState<Winner[]>([])
  // Until the list has loaded once the block is not drawn: an unread list is not "no winners yet".
  const [winnersLoaded, setWinnersLoaded] = useState(false)
  const [userTickets, setUserTickets] = useState<number[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [showClaimPrize, setShowClaimPrize] = useState(false)
  const [hasPendingPayout, setHasPendingPayout] = useState(false)
  const [pendingAmount, setPendingAmount] = useState<string | null>(null)
  const [showReferrerClaim, setShowReferrerClaim] = useState(false)
  const [hasPendingReferrer, setHasPendingReferrer] = useState(false)
  const [referrerAmount, setReferrerAmount] = useState<string | null>(null)
  const [referrerCount, setReferrerCount] = useState(1)
  const [referrerTotal, setReferrerTotal] = useState<string | null>(null)

  const fetchRound = useCallback(async () => {
    try {
      const data = await api.getCurrentRound()
      setRound(data)
      setError(null)
    } catch (err) {
      console.error('Failed to fetch round:', err)
      setError('Failed to load lottery data')
    }
  }, [])

  const fetchWinners = useCallback(async () => {
    try {
      const data = await api.getRecentWinners(6)
      if (!Array.isArray(data)) throw new Error('winners: not a list')
      setWinners(data)
      setWinnersLoaded(true)
    } catch (err) {
      // The list already on screen stays: a failed refresh does not empty it.
      console.error('Failed to fetch winners:', err)
    }
  }, [])

  const fetchUserTickets = useCallback(async () => {
    if (!round?.address || !user?.id) return
    try {
      const tickets = await api.getUserTickets(round.address, user.id)
      setUserTickets(Array.isArray(tickets) ? tickets : [])
    } catch (err) {
      console.error('Failed to fetch user tickets:', err)
      setUserTickets([])
    }
  }, [round?.address, user?.id])

  const checkPendingPayout = useCallback(async () => {
    if (!user?.id) return
    try {
      const payout = await api.getPendingPayout(user.id)
      setHasPendingPayout(payout.hasPending)
      setPendingAmount(payout.amount || null)
    } catch (err) {
      console.error('Failed to check pending payout:', err)
    }
  }, [user?.id])

  const checkPendingReferrer = useCallback(async () => {
    if (!user?.id) return
    try {
      const payout = await api.getPendingReferrerPayout(user.id)
      setHasPendingReferrer(payout.hasPending)
      setReferrerAmount(payout.amount || null)
      setReferrerCount(payout.count ?? 1)
      setReferrerTotal(payout.total ?? null)
    } catch (err) {
      console.error('Failed to check pending referrer payout:', err)
    }
  }, [user?.id])

  useEffect(() => {
    if (!isReady) return
    const loadData = async () => {
      setLoading(true)
      await Promise.all([fetchRound(), fetchWinners()])
      setLoading(false)
    }
    loadData()
    if (isTelegram) expand()
  }, [isReady, isTelegram, expand, fetchRound, fetchWinners])

  useEffect(() => {
    if (round && user) fetchUserTickets()
  }, [round, user, fetchUserTickets])

  useEffect(() => {
    if (user?.id) checkPendingPayout()
    if (user?.id) checkPendingReferrer()
  }, [user?.id, checkPendingPayout])

  useEffect(() => {
    const interval = setInterval(fetchRound, 30000)
    return () => clearInterval(interval)
  }, [fetchRound])

  const handlePurchaseSuccess = (ticketNumbers: number[]) => {
    setUserTickets((prev) => [...prev, ...ticketNumbers])
    fetchRound()
  }

  const handleClaimClose = () => {
    setShowClaimPrize(false)
    checkPendingPayout()
  }

  const handleReferrerClose = () => {
    setShowReferrerClaim(false)
    checkPendingReferrer()
  }

  if (loading) {
    return (
      <div className="min-h-screen flex flex-col">
        <Header />
        <main className="flex-1 px-4 py-6 space-y-4">
          <div className="card"><div className="h-32 shimmer rounded-lg" /></div>
          <div className="card"><div className="h-48 shimmer rounded-lg" /></div>
        </main>
      </div>
    )
  }

  if (error && !round) {
    return (
      <div className="min-h-screen flex flex-col">
        <Header />
        <main className="flex-1 px-4 py-6 flex items-center justify-center">
          <div className="text-center">
            <div className="text-5xl mb-4">😢</div>
            <div className="text-telegram-text mb-2">{error}</div>
            <button onClick={() => { setLoading(true); fetchRound().finally(() => setLoading(false)) }} className="btn-gold mt-4">Try Again</button>
          </div>
        </main>
      </div>
    )
  }

  const formattedWinners: Winner[] = Array.isArray(winners) ? winners.map((w, i) => ({
    ...w,
    place: ((i % 3) + 1) as 1 | 2 | 3,
  })) : []

  return (
    <div className="min-h-screen flex flex-col">
      <Header />
      <main className="flex-1 px-4 py-4 space-y-4 safe-bottom">
        {hasPendingPayout && (
          <div className="card bg-gradient-to-r from-yellow-500/20 to-orange-500/20 border border-gold">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-gold-500 font-bold">🎉 {t('claim.cardWon')}</div>
                <div className="text-telegram-text text-lg font-bold">${pendingAmount}</div>
              </div>
              <button onClick={() => setShowClaimPrize(true)} className="btn-gold">
                {t('claim.cardClaim')}
              </button>
            </div>
          </div>
        )}
        {hasPendingReferrer && (
          <div className="card bg-gradient-to-r from-purple-500/20 to-blue-500/20 border border-gold">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-gold-500 font-bold">🎁 {t('claim.cardReward')}</div>
                <div className="text-telegram-text text-lg font-bold">${referrerAmount}</div>
                {referrerCount > 1 && referrerTotal && (
                  <div className="text-xs text-telegram-hint">
                    {t('claim.cardRewardMore', { count: referrerCount, total: referrerTotal })}
                  </div>
                )}
              </div>
              <button onClick={() => setShowReferrerClaim(true)} className="btn-gold">
                {t('claim.cardClaimReward')}
              </button>
            </div>
          </div>
        )}
        {round && <RoundInfo pool={round.prizePool} participants={round.participantCount} endTime={round.endTime} state={round.state} />}
        {userTickets.length > 0 && round && <YourTickets tickets={userTickets} roundState={round.state} />}
        {round && <BuyTickets ticketPrice={round.ticketPrice} disabled={round?.state !== 'OPEN'} onSuccess={handlePurchaseSuccess} />}
        {user?.id && <InviteFriends telegramUserId={user.id} />}
        {winnersLoaded && <Winners winners={formattedWinners} loading={false} />}
        <div className="text-center py-4">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-telegram-secondary rounded-full">
            <span className="text-green-400">✓</span>
            <span className="text-xs text-telegram-hint">Powered by Chainlink VRF</span>
          </div>
        </div>
      </main>

      {showClaimPrize && user?.id && (
        <ClaimWizard kind="prize" amount={pendingAmount} claim={prizeClaim} onClose={handleClaimClose} />
      )}
      {showReferrerClaim && user?.id && (
        <ClaimWizard kind="reward" amount={referrerAmount} claim={rewardClaim} onClose={handleReferrerClose} />
      )}
    </div>
  )
}
